/**
 * Label lookup for the confirmation card: the one place that knows how to
 * say "Confirm", "Cancel", a boolean, or a write's field caption in the
 * chat's own language.
 *
 * The draft's `language` is an ISO 639-1 code, forwarded by the backend as
 * the turn's reply language. No live draft sends it yet (an older backend,
 * or one built before the backend added the field), so `resolveLanguage`
 * treats absent, empty or unsupported input as English — and English's own
 * tables below reproduce today's card output exactly, byte for byte,
 * so every existing caller is a no-op through this module.
 */

export type Language = 'en' | 'ar' | 'es' | 'fr';

const SUPPORTED_LANGUAGES: ReadonlySet<string> = new Set([
  'en',
  'ar',
  'es',
  'fr',
]);

export function resolveLanguage(language: unknown): Language {
  if (typeof language === 'string' && SUPPORTED_LANGUAGES.has(language)) {
    return language as Language;
  }
  return 'en';
}

/** "due_date" → "Due date" — the English fallback caption for any key this
 * module has no translation for, in any language. */
export function humanize(key: string): string {
  const words = key.split('_').join(' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

interface StaticLabels {
  confirm: string;
  cancel: string;
  waitingTitle: string;
  cancelFailed: string;
  confirmedBubble: string;
}

const STATIC_LABELS: Record<Language, StaticLabels> = {
  en: {
    confirm: 'Confirm',
    cancel: 'Cancel',
    waitingTitle: 'Waiting Your OK',
    cancelFailed: "Couldn't cancel — please try again.",
    confirmedBubble: 'Confirm',
  },
  ar: {
    confirm: 'تأكيد',
    cancel: 'إلغاء',
    waitingTitle: 'في انتظار موافقتك',
    cancelFailed: 'تعذر الإلغاء — حاول مرة أخرى.',
    confirmedBubble: 'تأكيد',
  },
  es: {
    confirm: 'Confirmar',
    cancel: 'Cancelar',
    // Fix round 3: formal-neutral ("usted") wording, not the informal "tu".
    waitingTitle: 'Esperando su confirmación',
    cancelFailed: 'No se pudo cancelar — inténtelo de nuevo.',
    confirmedBubble: 'Confirmar',
  },
  fr: {
    confirm: 'Confirmer',
    cancel: 'Annuler',
    waitingTitle: 'En attente de votre accord',
    cancelFailed: "Impossible d'annuler — veuillez réessayer.",
    confirmedBubble: 'Confirmer',
  },
};

export function staticLabel(
  language: Language,
  key: keyof StaticLabels
): string {
  return STATIC_LABELS[language][key];
}

/**
 * English keeps today's literal JS text ("true"/"false") rather than a
 * "Yes"/"No" word — that is the no-op path every caller without a
 * `language` still takes. Arabic/Spanish/French get the real word, since a
 * bare "true"/"false" is what this task set out to stop showing them.
 */
const BOOLEAN_LABELS: Record<Language, { true: string; false: string }> = {
  en: { true: 'true', false: 'false' },
  ar: { true: 'نعم', false: 'لا' },
  es: { true: 'Sí', false: 'No' },
  fr: { true: 'Oui', false: 'Non' },
};

export function booleanLabel(language: Language, value: boolean): string {
  return BOOLEAN_LABELS[language][value ? 'true' : 'false'];
}

/**
 * English overrides for keys whose `humanize()` caption is wrong or unclear
 * (e.g. backend typo `referal` → "Referal", `db_status` → "Db status").
 * Every other English key still falls through to `humanize()`.
 */
export const FIELD_CAPTIONS_EN: Record<string, string> = {
  referal: 'Referral',
  db_status: 'Task status',
};

/** Captions for the common write keys. Non-English entries live here;
 * English uses `FIELD_CAPTIONS_EN` then `humanize()`.
 *
 * Exported (fix round 1) so tests can assert `Object.hasOwn` directly on
 * each language's table — the only check that catches a key missing from
 * one specific table, rather than inferring it from the English fallback,
 * which a legitimate cognate can also trigger as a false signal. */
export const FIELD_CAPTIONS: Record<
  'ar' | 'es' | 'fr',
  Record<string, string>
> = {
  ar: {
    name: 'الاسم',
    first_name: 'الاسم الأول',
    last_name: 'اسم العائلة',
    email: 'البريد الإلكتروني',
    phone_number: 'رقم الهاتف',
    country: 'الدولة',
    city: 'المدينة',
    title: 'العنوان (الاسم)',
    description: 'الوصف',
    content: 'المحتوى',
    body: 'النص',
    note: 'ملاحظة',
    // Fix round 3, task 3 (90, backend titles): "الترتيب" ("ranking/order"),
    // distinct from the sort/order concept "التصنيف" collided with.
    rank: 'الترتيب',
    status: 'الحالة',
    timezone: 'المنطقة الزمنية',
    company_name: 'اسم الشركة',
    due_date: 'تاريخ الاستحقاق',
    date: 'التاريخ',
    amount: 'المبلغ',
    price: 'السعر',
    currency: 'العملة',
    type: 'النوع',
    role: 'الدور',
    is_public: 'عام',
    is_default: 'افتراضي',
    entity_type: 'نوع الكيان',
    display_name: 'الاسم المعروض',
    starred: 'مميز بنجمة',
    read: 'مقروء',
    until: 'حتى',
    programs: 'البرامج',
    permissions: 'الصلاحيات',
    // Fix round 3, task 1: every remaining visible argument key found in
    // real assistant_pending_actions rows (top-level, inside `values`, and
    // nested line_items entries).
    channel: 'قناة الاتصال',
    next_follow_up_date: 'تاريخ المتابعة القادمة',
    workspace: 'مساحة العمل',
    // Fix round 1 (Opus review, IMPORTANT): was generic "database status" —
    // staff read it as unrelated to the lead it actually describes.
    db_status: 'حالة المهمة',
    lead_ids: 'معرّفات العملاء المحتملين',
    program: 'البرنامج',
    student: 'الطالب',
    start_time: 'وقت البدء',
    end_time: 'وقت الانتهاء',
    priority: 'الأولوية',
    lead: 'العميل المحتمل',
    comment: 'التعليق',
    label: 'الوسم',
    send_email: 'إرسال إشعار بالبريد الإلكتروني',
    location: 'الموقع',
    // Fix round 1: shared HEX/HEXA pattern instead of two different phrasings.
    hex_color: 'رمز اللون (HEX)',
    hexa_color: 'رمز اللون (HEXA)',
    color: 'اللون',
    // Fix round 1: "bases" plural, matching the plural _ids key.
    kbase_ids: 'معرّفات قواعد المعرفة',
    // Fix round 3, task 3: a record noun ("submission request"), matching
    // 90's localised card title head — was "form sending" (a verb phrase).
    submission: 'طلب التقديم',
    leads: 'العملاء المحتملون',
    scope: 'النطاق',
    is_private: 'خاص',
    // Fix round 3, task 3: captioned as the record's TYPE (set_custom_field_
    // value's `entity` names which kind of record the field belongs to),
    // distinct from `entity_type` below.
    entity: 'نوع السجل',
    placements: 'التنسيبات',
    // Fix round 1: "mastery/proficiency level", not "competency level".
    proficiency: 'مستوى الإتقان',
    user: 'المستخدم',
    // Fix round 1: "invoice items/lines", a clearer noun than "elements".
    line_items: 'بنود الفاتورة',
    issued_to: 'صادرة إلى',
    member: 'العضو',
    member_ids: 'معرّفات الأعضاء',
    static_filters: 'المرشحات الثابتة',
    filters: 'المرشحات',
    arrival_airport: 'مطار الوصول',
    value: 'القيمة',
    invoice: 'الفاتورة',
    issuer: 'الجهة المصدرة',
    payment_date: 'تاريخ الدفع',
    cost: 'التكلفة',
    visa_result: 'نتيجة التأشيرة',
    channel_ids: 'معرّفات القنوات',
    major: 'التخصص',
    assignee: 'الشخص المكلّف',
    assignees: 'الأشخاص المكلّفون',
    technical_skills: 'المهارات التقنية',
    language: 'اللغة',
    // Fix round 1: dropped the uniqueness parenthetical.
    is_required: 'إلزامي',
    start_date: 'تاريخ البدء',
    end_date: 'تاريخ الانتهاء',
    section: 'القسم',
    field: 'الحقل',
    field_type: 'نوع الحقل',
    number_of_positions: 'عدد الشواغر',
    tag_ids: 'معرّفات الوسوم',
    position: 'المنصب',
    position_name: 'اسم المنصب',
    position_type: 'نوع المنصب',
    interview_type: 'نوع المقابلة',
    interview_date: 'تاريخ المقابلة',
    interview_time: 'وقت المقابلة',
    // Fix round 1: swapped so "resolved" reads as the plain state and
    // "is_resolved" (the boolean flag) reads as the explicit marking action.
    resolved: 'تم الحل',
    is_resolved: 'مُعلَّم كمحلول',
    notification_channel: 'قناة الإشعارات',
    notification_ids: 'معرّفات الإشعارات',
    phone: 'الهاتف',
    method: 'طريقة الدفع',
    labels: 'الوسوم',
    members: 'الأعضاء',
    contact: 'جهة الاتصال',
    // Fix round 1: keep SWIFT as the Latin acronym staff actually see on a
    // bank transfer form, instead of transliterating it.
    swift_code: 'رمز SWIFT',
    student_ids: 'معرّفات الطلاب',
    recipient_name: 'اسم المستلم',
    recipient_number: 'رقم المستلم',
    language_level: 'المستوى اللغوي',
    english_level: 'مستوى اللغة الإنجليزية',
    // Fix round 1: dropped the uniqueness parenthetical; "active" unchanged.
    is_active: 'مفعّل',
    active: 'نشط',
    // Fix round 1 (Opus review, IMPORTANT): this is a yes/no flag meaning
    // "answers only from its knowledge base" — "a scope defined for the
    // knowledge base" read as something else entirely.
    knowledge_scoped: 'مقيّد بقاعدة المعرفة',
    knowledge_documents: 'وثائق المعرفة',
    institution: 'المؤسسة التعليمية',
    // Fix round 1: IBAN as the Latin acronym, not transliterated.
    iban: 'رقم IBAN',
    salary: 'الراتب',
    // Fix round 1: "salary periodicity" is the precise term for "weekly /
    // monthly / yearly", not "type of salary period".
    salary_per_type: 'دورية الراتب',
    salary_currency: 'عملة الراتب',
    primary_email: 'البريد الإلكتروني الرئيسي',
    // Fix round 3, task 3: the short record noun 90's localised card title
    // head needs — was "track stage", a longer descriptive phrase.
    stage: 'المرحلة',
    issue_date: 'تاريخ الإصدار',
    earliest_start_date: 'أقرب تاريخ بدء ممكن',
    earliest_end_date: 'أقرب تاريخ انتهاء ممكن',
    latest_start_date: 'آخر تاريخ بدء ممكن',
    latest_end_date: 'آخر تاريخ انتهاء ممكن',
    visa_interview: 'مقابلة التأشيرة',
    achieving_criterion: 'معيار الاستيفاء',
    achieving_criterion_answer: 'إجابة معيار الاستيفاء',
    arrival_time: 'وقت الوصول',
    arrival_date: 'تاريخ الوصول',
    flight_number: 'رقم الرحلة',
    beneficiary_name: 'اسم المستفيد',
    bank_name: 'اسم البنك',
    account_number: 'رقم الحساب',
    soft_skills: 'المهارات الشخصية',
    computer_skills: 'مهارات الحاسوب',
    locked: 'مقفل',
    host: 'الجهة المستقبلة',
    degree: 'الدرجة العلمية',
    gender: 'الجنس',
    choices: 'الخيارات',
    provider_name: 'اسم مزوّد الخدمة',
    employment_type: 'نوع التوظيف',
    company: 'الشركة',
    job_title: 'المسمى الوظيفي',
    // Fix round 1: "send a notification to the student", the actual verb.
    notify_student: 'إرسال إشعار للطالب',
    is_pinned: 'مثبّت',
    quantity: 'الكمية',
    product: 'المنتج',
    // Fix round 3, task 3: record nouns 90's localised card title names
    // (looked up as `<key>` and then `<key minus _id>`).
    from_stage: 'المرحلة الحالية',
    to_stage: 'المرحلة الجديدة',
    meeting: 'الاجتماع',
    partner: 'الشريك',
    payment: 'الدفعة',
    placement: 'التنسيب',
    receipt: 'الإيصال',
    reminder: 'التذكير',
    // The student's CV.
    resume: 'السيرة الذاتية',
    task: 'المهمة',
    university: 'الجامعة',
    // Polish round: the saved-view record, not the bare "display".
    view: 'العرض المحفوظ',
    // Fix round 3, task 3: field names shown in "field: old → new" update
    // lines.
    // Polish round: a full sentence-style flag reading.
    ai_enabled: 'الذكاء الاصطناعي مفعّل',
    all_year: 'على مدار العام',
    answer: 'الإجابة',
    application_deadline: 'الموعد النهائي للتقديم',
    avatar_url: 'رابط الصورة الرمزية',
    // date_of_birth / birth_date: two backend fields for the same concept —
    // genuine Arabic synonyms, same "تاريخ ال..." shape as hex/hexa.
    birth_date: 'تاريخ الولادة',
    contact_email: 'البريد الإلكتروني لجهة الاتصال',
    contact_name: 'اسم جهة الاتصال',
    date_of_birth: 'تاريخ الميلاد',
    deadline: 'الموعد النهائي',
    decline_reason: 'سبب الرفض',
    default_ai_agent: 'وكيل الذكاء الاصطناعي الافتراضي',
    department: 'الإدارة',
    disabled: 'غير مفعّل',
    discount_type: 'نوع الخصم',
    discount_value: 'قيمة الخصم',
    is_blocked: 'محظور',
    // Polish round: distinct from is_active's "مفعّل"/active's "نشط".
    is_enabled: 'مُمكَّن',
    is_free: 'مجاني',
    is_primary: 'رئيسي',
    // Polish round: "lifecycle stage", distinct from stage/from_stage/
    // to_stage.
    lifecycle: 'مرحلة دورة الحياة',
    link: 'الرابط',
    main_contact_email: 'البريد الإلكتروني لجهة الاتصال الرئيسية',
    main_contact_name: 'اسم جهة الاتصال الرئيسية',
    main_contact_phone: 'رقم هاتف جهة الاتصال الرئيسية',
    options: 'خيارات الحقل',
    position_description: 'وصف المنصب',
    primary_phone_number: 'رقم الهاتف الرئيسي',
    program_ids: 'معرّفات البرامج',
    // referal (sic, backend key): an organisation-defined referral entry
    // (LeadReferal, lead.py:237) — not the lead who referred this one.
    referal: 'جهة الإحالة',
    secondary_email: 'البريد الإلكتروني الثانوي',
    secondary_phone_number: 'رقم الهاتف الثانوي',
    source: 'المصدر',
    source_note: 'ملاحظة المصدر',
    subject: 'الموضوع',
    website: 'الموقع الإلكتروني',
    whatsapp_number: 'رقم واتساب',
    // Document import (problem-codes.md): `required` names this pseudo-field
    // when a row has neither an email nor a phone.
    email_or_phone: 'البريد الإلكتروني أو الهاتف',
    // Document import: field_not_allowed names one of a fixed set of labels
    // (assignee, owner, status, stage, program(s), tags, labels, id, other).
    owner: 'المالك',
    // Fix round 4: was "العلامات" (root علامة, "marks" — evokes
    // "trademarks"). The app's own vocabulary for tags is the وسم root
    // (labels → "الوسوم", tag_ids → "معرّفات الوسوم", inbox tags), so tags
    // uses that root too: "the contact's tags", since an import's tags
    // apply to the imported record/contact — kept distinct from labels'
    // "الوسوم".
    tags: 'وسوم جهة الاتصال',
    id: 'المعرّف',
    other: 'عمود آخر',
    conversation: 'المحادثة',
    // The duplicate contact in a merge.
    duplicate: 'جهة الاتصال المكررة',
  },
  es: {
    name: 'Nombre',
    // Fix round 1: "Nombre" alone collided with `name`'s caption — a card
    // carrying both fields was impossible to tell apart.
    first_name: 'Nombre de pila',
    last_name: 'Apellido',
    email: 'Correo electrónico',
    phone_number: 'Número de teléfono',
    country: 'País',
    city: 'Ciudad',
    title: 'Título',
    description: 'Descripción',
    content: 'Contenido',
    body: 'Cuerpo',
    note: 'Nota',
    rank: 'Clasificación',
    status: 'Estado',
    timezone: 'Zona horaria',
    company_name: 'Nombre de la empresa',
    due_date: 'Fecha de vencimiento',
    date: 'Fecha',
    amount: 'Importe',
    price: 'Precio',
    currency: 'Moneda',
    type: 'Tipo',
    role: 'Rol',
    is_public: 'Público',
    is_default: 'Predeterminado',
    entity_type: 'Tipo de entidad',
    display_name: 'Nombre para mostrar',
    starred: 'Destacado',
    read: 'Leído',
    until: 'Hasta',
    programs: 'Programas',
    permissions: 'Permisos',
    // Fix round 3, task 1: every remaining visible argument key found in
    // real assistant_pending_actions rows (top-level, inside `values`, and
    // nested line_items entries).
    channel: 'Canal de comunicación',
    next_follow_up_date: 'Fecha del próximo seguimiento',
    workspace: 'Espacio de trabajo',
    // Fix round 1 (Opus review, IMPORTANT): was generic "database status" —
    // staff read it as unrelated to the lead it actually describes.
    db_status: 'Estado de la tarea',
    // Fix round 1: consistent with "Cliente potencial"/"Clientes
    // potenciales" elsewhere in this table, instead of the bare loanword.
    lead_ids: 'Identificadores de clientes potenciales',
    program: 'Programa',
    student: 'Estudiante',
    start_time: 'Hora de inicio',
    end_time: 'Hora de finalización',
    priority: 'Prioridad',
    lead: 'Cliente potencial',
    comment: 'Comentario',
    label: 'Etiqueta',
    send_email: 'Enviar notificación por correo electrónico',
    location: 'Ubicación',
    // Fix round 1: shared HEX/HEXA pattern instead of two different phrasings.
    hex_color: 'Código de color (HEX)',
    hexa_color: 'Código de color (HEXA)',
    // Allowed cognate (see card-labels.test.ts's COGNATE_ALLOWLIST): "color"
    // is the actual Spanish word, spelled the same as the English caption.
    color: 'Color',
    // Fix round 1: "bases" plural, matching the plural _ids key.
    kbase_ids: 'Identificadores de las bases de conocimientos',
    // Fix round 3, task 3: a record noun matching 90's localised card title
    // head — was "form submission" (a process/action phrase).
    submission: 'Solicitud',
    leads: 'Clientes potenciales',
    scope: 'Alcance',
    is_private: 'Privado',
    // Fix round 3, task 3: captioned as the record's TYPE, distinct from
    // entity_type below.
    entity: 'Tipo de registro',
    // Fix round 1 (Opus review, IMPORTANT).
    placements: 'Colocaciones',
    // Fix round 1: "mastery/proficiency level", not "competency level".
    proficiency: 'Nivel de dominio',
    user: 'Usuario',
    // Fix round 1.
    line_items: 'Conceptos',
    issued_to: 'Emitida a',
    member: 'Miembro',
    member_ids: 'Identificadores de miembros',
    static_filters: 'Filtros fijos',
    filters: 'Filtros',
    arrival_airport: 'Aeropuerto de llegada',
    value: 'Valor',
    invoice: 'Factura',
    issuer: 'Emisor',
    payment_date: 'Fecha de pago',
    cost: 'Costo',
    visa_result: 'Resultado de la visa',
    channel_ids: 'Identificadores de canales',
    major: 'Especialidad',
    assignee: 'Persona asignada',
    assignees: 'Personas asignadas',
    technical_skills: 'Habilidades técnicas',
    language: 'Idioma',
    is_required: 'Obligatorio',
    start_date: 'Fecha de inicio',
    end_date: 'Fecha de finalización',
    section: 'Sección',
    field: 'Campo',
    field_type: 'Tipo de campo',
    // Fix round 1.
    number_of_positions: 'Número de vacantes',
    tag_ids: 'Identificadores de etiquetas',
    position: 'Puesto',
    position_name: 'Nombre del puesto',
    position_type: 'Tipo de puesto',
    interview_type: 'Tipo de entrevista',
    interview_date: 'Fecha de la entrevista',
    interview_time: 'Hora de la entrevista',
    resolved: 'Resuelto',
    is_resolved: 'Marcado como resuelto',
    notification_channel: 'Canal de notificación',
    notification_ids: 'Identificadores de notificaciones',
    phone: 'Teléfono',
    method: 'Método de pago',
    labels: 'Etiquetas',
    members: 'Miembros',
    contact: 'Contacto',
    swift_code: 'Código SWIFT',
    student_ids: 'Identificadores de estudiantes',
    recipient_name: 'Nombre del destinatario',
    recipient_number: 'Número del destinatario',
    language_level: 'Nivel de idioma',
    english_level: 'Nivel de inglés',
    // Fix round 1: dropped the uniqueness parenthetical; "active" unchanged.
    is_active: 'Habilitado',
    active: 'Activo',
    // Fix round 1 (Opus review, IMPORTANT): this is a yes/no flag meaning
    // "answers only from its knowledge base".
    knowledge_scoped: 'Limitado a la base de conocimientos',
    knowledge_documents: 'Documentos de la base de conocimientos',
    institution: 'Institución',
    iban: 'IBAN',
    salary: 'Salario',
    salary_per_type: 'Periodicidad del salario',
    salary_currency: 'Moneda del salario',
    primary_email: 'Correo electrónico principal',
    stage: 'Etapa',
    issue_date: 'Fecha de emisión',
    earliest_start_date: 'Fecha de inicio más temprana',
    earliest_end_date: 'Fecha de finalización más temprana',
    latest_start_date: 'Fecha de inicio más tardía',
    latest_end_date: 'Fecha de finalización más tardía',
    visa_interview: 'Entrevista de visa',
    achieving_criterion: 'Criterio de cumplimiento',
    achieving_criterion_answer: 'Respuesta al criterio de cumplimiento',
    arrival_time: 'Hora de llegada',
    arrival_date: 'Fecha de llegada',
    flight_number: 'Número de vuelo',
    beneficiary_name: 'Nombre del beneficiario',
    bank_name: 'Nombre del banco',
    account_number: 'Número de cuenta',
    soft_skills: 'Habilidades blandas',
    computer_skills: 'Habilidades informáticas',
    locked: 'Bloqueado',
    // Fix round 1 (Opus review, IMPORTANT): the host employer/organisation
    // of a placement, not a social "host" — "Anfitrión" misled staff.
    host: 'Entidad de acogida',
    degree: 'Título académico',
    gender: 'Género',
    choices: 'Opciones',
    provider_name: 'Nombre del proveedor',
    employment_type: 'Tipo de empleo',
    company: 'Empresa',
    // Fix round 1.
    job_title: 'Cargo',
    notify_student: 'Notificar al estudiante',
    is_pinned: 'Fijado',
    quantity: 'Cantidad',
    product: 'Producto',
    // Fix round 3, task 3: record nouns 90's localised card title names
    // (looked up as `<key>` and then `<key minus _id>`).
    // Polish round: reworded to "origin stage".
    from_stage: 'Etapa de origen',
    to_stage: 'Nueva etapa',
    meeting: 'Reunión',
    // Polish round: a collaborating partner organisation.
    partner: 'Socio colaborador',
    payment: 'Pago',
    placement: 'Colocación',
    receipt: 'Recibo',
    reminder: 'Recordatorio',
    // The student's CV.
    resume: 'Currículum',
    task: 'Tarea',
    university: 'Universidad',
    view: 'Vista',
    // Fix round 3, task 3: field names shown in "field: old → new" update
    // lines.
    ai_enabled: 'IA habilitada',
    all_year: 'Todo el año',
    answer: 'Respuesta',
    application_deadline: 'Fecha límite de solicitud',
    avatar_url: 'URL del avatar',
    // date_of_birth / birth_date: two backend fields for the same concept —
    // Polish round: shortened to the bare noun.
    birth_date: 'Nacimiento',
    contact_email: 'Correo electrónico de contacto',
    contact_name: 'Nombre de contacto',
    date_of_birth: 'Fecha de nacimiento',
    deadline: 'Fecha límite',
    decline_reason: 'Motivo de rechazo',
    default_ai_agent: 'Agente de IA predeterminado',
    department: 'Departamento',
    disabled: 'Deshabilitado',
    discount_type: 'Tipo de descuento',
    discount_value: 'Valor del descuento',
    // A blocked inbox contact — distinct from "locked" above.
    is_blocked: 'Contacto bloqueado',
    // Polish round: distinct from is_active's "Habilitado"/active's "Activo".
    is_enabled: 'Activado',
    is_free: 'Gratuito',
    is_primary: 'Principal',
    // Polish round: "lifecycle stage", distinct from stage/from_stage/
    // to_stage.
    lifecycle: 'Etapa del ciclo de vida',
    link: 'Enlace',
    main_contact_email: 'Correo electrónico del contacto principal',
    main_contact_name: 'Nombre del contacto principal',
    main_contact_phone: 'Teléfono del contacto principal',
    options: 'Opciones del campo',
    position_description: 'Descripción del puesto',
    primary_phone_number: 'Número de teléfono principal',
    program_ids: 'Identificadores de programas',
    // referal (sic, backend key): an organisation-defined referral entry
    // (LeadReferal, lead.py:237) — not the lead who referred this one.
    referal: 'Referido por',
    secondary_email: 'Correo electrónico secundario',
    secondary_phone_number: 'Número de teléfono secundario',
    source: 'Origen',
    source_note: 'Nota sobre el origen',
    subject: 'Asunto',
    website: 'Sitio web',
    whatsapp_number: 'Número de WhatsApp',
    // Document import: `required`'s pseudo-field (neither email nor phone).
    email_or_phone: 'Correo electrónico o teléfono',
    // Document import: field_not_allowed's fixed labels.
    owner: 'Propietario',
    // Distinct from labels' "Etiquetas".
    tags: 'Palabras clave',
    id: 'Identificador',
    other: 'Otra columna',
    conversation: 'Conversación',
    // The duplicate contact in a merge.
    duplicate: 'Contacto duplicado',
  },
  fr: {
    name: 'Nom',
    first_name: 'Prénom',
    last_name: 'Nom de famille',
    email: 'E-mail',
    phone_number: 'Numéro de téléphone',
    country: 'Pays',
    city: 'Ville',
    title: 'Titre',
    description: 'Description',
    content: 'Contenu',
    body: 'Corps',
    note: 'Note',
    rank: 'Classement',
    status: 'Statut',
    timezone: 'Fuseau horaire',
    company_name: "Nom de l'entreprise",
    due_date: "Date d'échéance",
    date: 'Date',
    amount: 'Montant',
    price: 'Prix',
    currency: 'Devise',
    type: 'Type',
    role: 'Rôle',
    is_public: 'Public',
    is_default: 'Par défaut',
    entity_type: "Type d'entité",
    display_name: "Nom d'affichage",
    starred: 'Favori',
    read: 'Lu',
    until: "Jusqu'à",
    programs: 'Programmes',
    permissions: 'Autorisations',
    // Fix round 3, task 1: every remaining visible argument key found in
    // real assistant_pending_actions rows (top-level, inside `values`, and
    // nested line_items entries).
    channel: 'Canal de communication',
    next_follow_up_date: 'Date du prochain suivi',
    workspace: 'Espace de travail',
    // Fix round 1 (Opus review, IMPORTANT): was generic "database status" —
    // staff read it as unrelated to the lead it actually describes.
    db_status: 'Statut de la tâche',
    lead_ids: 'Identifiants des prospects',
    program: 'Programme',
    student: 'Étudiant',
    start_time: 'Heure de début',
    end_time: 'Heure de fin',
    priority: 'Priorité',
    lead: 'Prospect',
    comment: 'Commentaire',
    label: 'Étiquette',
    send_email: 'Envoyer une notification par e-mail',
    // Fix round 1.
    location: 'Lieu',
    // Fix round 1: shared HEX/HEXA pattern instead of two different phrasings.
    hex_color: 'Code couleur (HEX)',
    hexa_color: 'Code couleur (HEXA)',
    color: 'Couleur',
    // Fix round 1: "bases" plural, matching the plural _ids key.
    kbase_ids: 'Identifiants des bases de connaissances',
    // Fix round 3, task 3: a record noun matching 90's localised card title
    // head — was "submission" the process noun ("submitting"), ambiguous
    // next to a quoted name.
    submission: 'Candidature',
    leads: 'Prospects',
    scope: 'Portée',
    is_private: 'Privé',
    // Fix round 3, task 3: captioned as the record's TYPE, distinct from
    // entity_type below.
    entity: "Type d'enregistrement",
    placements: 'Affectations',
    // Fix round 1: "mastery/proficiency level", not "competency level".
    proficiency: 'Niveau de maîtrise',
    user: 'Utilisateur',
    line_items: 'Lignes de facture',
    issued_to: 'Émise à',
    member: 'Membre',
    member_ids: 'Identifiants des membres',
    static_filters: 'Filtres fixes',
    filters: 'Filtres',
    arrival_airport: "Aéroport d'arrivée",
    value: 'Valeur',
    invoice: 'Facture',
    issuer: 'Émetteur',
    payment_date: 'Date de paiement',
    cost: 'Coût',
    visa_result: 'Résultat du visa',
    channel_ids: 'Identifiants des canaux',
    major: 'Spécialité',
    assignee: 'Personne assignée',
    assignees: 'Personnes assignées',
    technical_skills: 'Compétences techniques',
    language: 'Langue',
    is_required: 'Obligatoire',
    start_date: 'Date de début',
    end_date: 'Date de fin',
    // Fix round 1 (Opus review): "Section" is the natural French word, a
    // true cognate — allow-listed in card-labels.test.ts's
    // COGNATE_ALLOWLIST rather than forced into an unnatural alternative.
    section: 'Section',
    field: 'Champ',
    field_type: 'Type de champ',
    number_of_positions: 'Nombre de postes',
    tag_ids: 'Identifiants des étiquettes',
    position: 'Poste',
    position_name: 'Nom du poste',
    position_type: 'Type de poste',
    interview_type: "Type d'entretien",
    interview_date: "Date de l'entretien",
    interview_time: "Heure de l'entretien",
    resolved: 'Résolu',
    is_resolved: 'Marqué comme résolu',
    notification_channel: 'Canal de notification',
    notification_ids: 'Identifiants des notifications',
    phone: 'Téléphone',
    // Fix round 1.
    method: 'Moyen de paiement',
    labels: 'Étiquettes',
    members: 'Membres',
    // Fix round 1 (Opus review): "Contact" is the natural French word, a
    // true cognate — allow-listed in card-labels.test.ts's
    // COGNATE_ALLOWLIST rather than forced into an unnatural alternative.
    contact: 'Contact',
    swift_code: 'Code SWIFT',
    student_ids: 'Identifiants des étudiants',
    recipient_name: 'Nom du destinataire',
    recipient_number: 'Numéro du destinataire',
    language_level: 'Niveau de langue',
    english_level: "Niveau d'anglais",
    // Fix round 1: dropped the uniqueness parenthetical; "active" unchanged.
    is_active: 'Activé',
    active: 'Actif',
    // Fix round 1 (Opus review, IMPORTANT): this is a yes/no flag meaning
    // "answers only from its knowledge base".
    knowledge_scoped: 'Limité à la base de connaissances',
    knowledge_documents: 'Documents de la base de connaissances',
    institution: 'Établissement',
    iban: 'IBAN',
    salary: 'Salaire',
    salary_per_type: 'Périodicité du salaire',
    salary_currency: 'Devise du salaire',
    primary_email: 'E-mail principal',
    stage: 'Étape',
    issue_date: "Date d'émission",
    // Fix round 1.
    earliest_start_date: 'Date de début au plus tôt',
    earliest_end_date: 'Date de fin au plus tôt',
    latest_start_date: 'Date de début au plus tard',
    latest_end_date: 'Date de fin au plus tard',
    visa_interview: 'Entretien de visa',
    // Fix round 1.
    achieving_criterion: 'Critère à remplir',
    achieving_criterion_answer: 'Réponse au critère à remplir',
    arrival_time: "Heure d'arrivée",
    arrival_date: "Date d'arrivée",
    flight_number: 'Numéro de vol',
    beneficiary_name: 'Nom du bénéficiaire',
    bank_name: 'Nom de la banque',
    account_number: 'Numéro de compte',
    soft_skills: 'Compétences comportementales',
    computer_skills: 'Compétences informatiques',
    locked: 'Verrouillé',
    // Fix round 1 (Opus review, IMPORTANT): the host employer/organisation
    // of a placement, not a social "host" — "Hôte" misled staff.
    host: "Structure d'accueil",
    degree: 'Diplôme',
    gender: 'Genre',
    choices: 'Choix',
    provider_name: 'Nom du prestataire',
    employment_type: "Type d'emploi",
    company: 'Entreprise',
    job_title: 'Intitulé du poste',
    notify_student: "Notifier l'étudiant",
    is_pinned: 'Épinglé',
    quantity: 'Quantité',
    product: 'Produit',
    // Fix round 3, task 3: record nouns 90's localised card title names
    // (looked up as `<key>` and then `<key minus _id>`).
    // Polish round: reworded to "origin stage".
    from_stage: "Étape d'origine",
    to_stage: 'Nouvelle étape',
    meeting: 'Réunion',
    partner: 'Partenaire',
    payment: 'Paiement',
    placement: 'Affectation',
    receipt: 'Reçu',
    reminder: 'Rappel',
    // The student's CV.
    resume: 'CV',
    task: 'Tâche',
    university: 'Université',
    view: 'Vue',
    // Fix round 3, task 3: field names shown in "field: old → new" update
    // lines.
    ai_enabled: 'IA activée',
    all_year: "Toute l'année",
    answer: 'Réponse',
    application_deadline: 'Date limite de candidature',
    avatar_url: "URL de l'avatar",
    // date_of_birth / birth_date: two backend fields for the same concept —
    // Polish round: shortened to the bare noun.
    birth_date: 'Naissance',
    contact_email: 'E-mail du contact',
    contact_name: 'Nom du contact',
    date_of_birth: 'Date de naissance',
    deadline: 'Date limite',
    decline_reason: 'Motif de refus',
    default_ai_agent: 'Agent IA par défaut',
    // Polish round: the organisational division — the literal French word.
    department: 'Département',
    disabled: 'Désactivé',
    discount_type: 'Type de remise',
    discount_value: 'Valeur de la remise',
    is_blocked: 'Bloqué',
    is_enabled: 'En service',
    is_free: 'Gratuit',
    is_primary: 'Principal',
    // Polish round: "lifecycle stage", distinct from stage/from_stage/
    // to_stage.
    lifecycle: 'Étape du cycle de vie',
    link: 'Lien',
    main_contact_email: 'E-mail du contact principal',
    main_contact_name: 'Nom du contact principal',
    main_contact_phone: 'Téléphone du contact principal',
    options: 'Options',
    position_description: 'Description du poste',
    primary_phone_number: 'Numéro de téléphone principal',
    program_ids: 'Identifiants des programmes',
    // referal (sic, backend key): an organisation-defined referral entry
    // (LeadReferal, lead.py:237) — not the lead who referred this one.
    referal: 'Recommandé par',
    secondary_email: 'E-mail secondaire',
    secondary_phone_number: 'Numéro de téléphone secondaire',
    source: 'Source',
    source_note: 'Note sur la source',
    subject: 'Objet',
    website: 'Site web',
    whatsapp_number: 'Numéro WhatsApp',
    // Document import: `required`'s pseudo-field (neither email nor phone).
    email_or_phone: 'E-mail ou téléphone',
    // Document import: field_not_allowed's fixed labels.
    owner: 'Propriétaire',
    // Distinct from labels' "Étiquettes".
    tags: 'Mots-clés',
    id: 'Identifiant',
    other: 'Autre colonne',
    // True cognate (see card-labels.test.ts's COGNATE_ALLOWLIST): French
    // "Conversation" is the same word, same spelling.
    conversation: 'Conversation',
    // The duplicate contact in a merge.
    duplicate: 'Contact en double',
  },
};

/** The common write keys this module has a translation for, in any
 * language — exported so callers (and tests) can check every key renders a
 * distinct caption within a language, without hand-copying the list.
 *
 * Fix round 3: derived from the UNION of all three tables, not just `ar` —
 * a key present in only one or two tables would otherwise escape the
 * uniqueness check for whichever table(s) it was missing from. */
export const KNOWN_FIELD_KEYS: readonly string[] = Array.from(
  new Set([
    ...Object.keys(FIELD_CAPTIONS.ar),
    ...Object.keys(FIELD_CAPTIONS.es),
    ...Object.keys(FIELD_CAPTIONS.fr),
  ])
);

/** An unmapped key — any key not in the table above, in any language —
 * never shows raw snake_case: it falls back to the English humanized
 * caption, same as it always has. */
export function fieldCaption(language: Language, key: string): string {
  if (language === 'en') return FIELD_CAPTIONS_EN[key] ?? humanize(key);
  return FIELD_CAPTIONS[language][key] ?? humanize(key);
}

/** Only Arabic reads right-to-left; English, Spanish and French stay LTR. */
export function isRtl(language: Language): boolean {
  return language === 'ar';
}
